import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateSignupForm, getPasswordStrength } from '../../utils/validation';
import { usePostHog } from '@posthog/react';

const SignupForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    company_name: '',
    user_type: '',
    user_type_other: '',
    terms_accepted: false,
    privacy_accepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const { register, handlePostAuthRedirect } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const renderTurnstile = useCallback(() => {
    if (turnstileRef.current && window.turnstile && turnstileWidgetId.current === null) {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        size: 'normal',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
      });
    }
  }, [turnstileSiteKey]);

  useEffect(() => {
    posthog.capture('signup_page_viewed');
  }, [posthog]);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    // If the script is already loaded, render immediately
    if (window.turnstile) {
      renderTurnstile();
      return;
    }
    // Otherwise wait for it to load
    const interval = setInterval(() => {
      if (window.turnstile) {
        renderTurnstile();
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [turnstileSiteKey, renderTurnstile]);

  const passwordStrength = getPasswordStrength(formData.password);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear this field's error and any general error together, so neither update overwrites the other
    if (errors[name] || errors.general) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
        general: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    setErrors({});

    const validationErrors = validateSignupForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      if (turnstileSiteKey && !turnstileToken) {
        setErrors({ general: 'Please complete the human verification check.' });
        setLoading(false);
        return;
      }

      const payload = turnstileToken
        ? { ...formData, turnstile_token: turnstileToken }
        : formData;
      const result = await register(payload);
      posthog.capture('user_signed_up', {
        has_company: !!formData.company_name,
      });
      const redirectIntent = handlePostAuthRedirect();

      navigate('/verify-email', {
        state: {
          userId: result.user_id,
          email: result.email,
          redirectIntent: redirectIntent
        }
      });
    } catch (err) {
      console.error('Registration error:', err);
      
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else if (err.response?.data?.error) {
        setErrors({ general: err.response.data.error });
      } else if (err.message) {
        setErrors({ general: err.message });
      } else {
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-8">
        <div>
          <h2 className="mt-4 text-center text-xl font-extrabold text-gray-900 sm:mt-6 sm:text-2xl md:text-3xl">
            Create your free account
          </h2>
        </div>
        <form className="mt-4 space-y-4 sm:mt-8 sm:space-y-6" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm sm:px-4 sm:py-3">
              {errors.general}
            </div>
          )}

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              <div>
                <label htmlFor="first_name" className="block text-xs font-medium text-gray-700 sm:text-sm">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  className={`input-field ${errors.first_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  value={formData.first_name}
                  onChange={handleChange}
                />
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>

              <div>
                <label htmlFor="last_name" className="block text-xs font-medium text-gray-700 sm:text-sm">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  className={`input-field ${errors.last_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  value={formData.last_name}
                  onChange={handleChange}
                />
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="company_name" className="block text-xs font-medium text-gray-700 sm:text-sm">
                Farm / Company Name
              </label>
              <input
                id="company_name"
                name="company_name"
                type="text"
                className={`input-field ${errors.company_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter your farm or company name"
              />
              {errors.company_name && <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>}
            </div>

            <div>
              <label htmlFor="user_type" className="block text-xs font-medium text-gray-700 sm:text-sm">
                Who are you? <span className="text-red-500">*</span>
              </label>
              <select
                id="user_type"
                name="user_type"
                required
                value={formData.user_type}
                onChange={handleChange}
                className={`input-field ${errors.user_type ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
              >
                <option value="">Select...</option>
                <option value="farmer">Farmer</option>
                <option value="advisor">Advisor</option>
                <option value="other">Other</option>
              </select>
              {errors.user_type && <p className="text-red-500 text-xs mt-1">{errors.user_type}</p>}
            </div>

            {formData.user_type === 'other' && (
              <div>
                <label htmlFor="user_type_other" className="block text-xs font-medium text-gray-700 sm:text-sm">
                  Please describe your role <span className="text-red-500">*</span>
                </label>
                <input
                  id="user_type_other"
                  name="user_type_other"
                  type="text"
                  required
                  value={formData.user_type_other}
                  onChange={handleChange}
                  placeholder="e.g. Land manager, researcher..."
                  className={`input-field ${errors.user_type_other ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {errors.user_type_other && <p className="text-red-500 text-xs mt-1">{errors.user_type_other}</p>}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-700 sm:text-sm">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={`input-field ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700 sm:text-sm">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className={`input-field pr-10 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setShowPasswordStrength(true)}
                  onBlur={() => setShowPasswordStrength(false)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              
              {/* Password strength indicator */}
              {(showPasswordStrength || formData.password) && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength.color === 'red' ? 'bg-red-500' :
                          passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                          passwordStrength.color === 'green' ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.color === 'red' ? 'text-red-600' :
                      passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                      passwordStrength.color === 'green' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {passwordStrength.text}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="mt-1 text-xs text-gray-500">
                Password must contain at least 8 characters including uppercase, lowercase, number, and special character.
              </div>
            </div>

            <div>
              <label htmlFor="password_confirm" className="block text-xs font-medium text-gray-700 sm:text-sm">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="password_confirm"
                  name="password_confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  required
                  className={`input-field pr-10 ${errors.password_confirm ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  value={formData.password_confirm}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  aria-label={showPasswordConfirm ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPasswordConfirm ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password_confirm && <p className="text-red-500 text-xs mt-1">{errors.password_confirm}</p>}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex items-center h-5 shrink-0">
              <input
                id="terms_accepted"
                name="terms_accepted"
                type="checkbox"
                required
                checked={formData.terms_accepted}
                onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                className="h-4 w-4 border-gray-300 rounded focus:ring-2 focus:ring-offset-2"
                style={{ accentColor: '#615DEC' }}
              />
            </div>
            <div className="text-xs sm:text-sm">
              <label htmlFor="terms_accepted" className="text-gray-700">
                I agree to the{' '}
                <a
                  href="https://docs.google.com/document/d/e/2PACX-1vSM47v3KKFy7Q4t_dWNNqF4U6mIWwhoxmdLGsOXubXoyz-7UXfH3eSyqRY4AzMP4Ahn_YNdAGbo3LhV/pub"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline"
                  style={{ color: '#615DEC' }}
                >
                  Terms and Conditions
                </a>
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex items-center h-5 shrink-0">
              <input
                id="privacy_accepted"
                name="privacy_accepted"
                type="checkbox"
                required
                checked={formData.privacy_accepted}
                onChange={(e) => setFormData({ ...formData, privacy_accepted: e.target.checked })}
                className="h-4 w-4 border-gray-300 rounded focus:ring-2 focus:ring-offset-2"
                style={{ accentColor: '#615DEC' }}
              />
            </div>
            <div className="text-xs sm:text-sm">
              <label htmlFor="privacy_accepted" className="text-gray-700">
                I agree to the{' '}
                <a
                  href="https://docs.google.com/document/d/1ntgM5oMaZO2TRpytwQ1yGPVUd5bdf3sWasZwEoGlVQY/edit?tab=t.0#heading=h.r80ciz5zjme1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium hover:underline"
                  style={{ color: '#615DEC' }}
                >
                  Privacy Policy
                </a>
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>

          {turnstileSiteKey && <div ref={turnstileRef} />}

          <div className="w-full">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>

          <div className="text-center">
            <span className="text-xs text-gray-600 sm:text-sm">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium hover:underline"
                style={{ color: '#615DEC' }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#4c47c7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#615DEC';
                }}
              >
                Sign in
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupForm;